import { authRef, hubRef } from "config/aws"
import { RECEIVE_USER_AUTH } from "./types"
import api from "../../constants/api"
import { fetchLayoutDetails } from "./fetchers"

export const initAuthObserver = () => async (dispatch) => {
	// Runs after a page refresh
	hubRef.listen("auth", async ({ payload }) => {
		const { event, data } = payload
		switch (event) {
			case "signIn": {
				const groups = (((data["signInUserSession"] || {}).accessToken || {}).payload || {})["cognito:groups"] || []
				dispatch({
					type: RECEIVE_USER_AUTH,
					payload: {
						attributes: await authRef.userAttributes(data),
						groups,
					},
				})
				break
			}
			case "signOut":
				dispatch({
					type: RECEIVE_USER_AUTH,
					payload: null,
				})
				break
			case "signUp":
			case "signIn_failure":
			case "configured":
			case "tokenRefresh":
			case "forgotPassword":
			case "forgotPasswordSubmit":
			case "completeNewPassword_failure":
				break
			case "signedOutUserPoolsTokenInvalid":
				console.error("signedOutUserPoolsTokenInvalid need to login again.")
				break
			default:
				console.error("Unsupported authentication event:", payload)
				break
		}
	})

	let user = null
	try {
		user = await authRef.currentAuthenticatedUser()
	} catch (err) {
		console.warn("currentAuthenticatedUser:", err)
		dispatch({
			type: RECEIVE_USER_AUTH,
			payload: null,
		})
	}
	if (user) {
		const userSession = user["signInUserSession"]

		const idToken = extractIdToken(userSession)
		if (idToken) {
			api.setIdTokens(idToken)
		}

		// An object containing a JWT token used to access graphQL queries/mutations and a payload object containing
		// auth-related user metadata
		const accessToken = userSession["accessToken"] || {}
		const groups = (accessToken["payload"] || {})["cognito:groups"] || []

		if (!user || !user.attributes) {
			console.error("No user or user.attributes:", user)
			return
		}

		let attributes = Object.keys(user.attributes).map((key) => ({
			Name: key,
			Value: user.attributes[key],
		}))
		dispatch({
			type: RECEIVE_USER_AUTH,
			payload: {
				attributes,
				groups,
			},
		})

		try {
			user = await authRef.currentAuthenticatedUser({ bypassCache: true })
		} catch (err) {
			console.error("NETWORK currentAuthenticatedUser", err)
		}
		attributes = null
		try {
			attributes = await authRef.userAttributes(user)
		} catch (err) {
			console.log("Error userAttributes:", err)
		}
		if (attributes) {
			dispatch({
				type: RECEIVE_USER_AUTH,
				payload: {
					attributes,
					groups,
				},
			})
		}
	}
}

function extractIdToken(cognitoUserSession) {
	if (!cognitoUserSession) return null
	if (cognitoUserSession) {
		const idToken = cognitoUserSession["idToken"]
		if (idToken) {
			return idToken
		} else {
			console.error("Received no id token:", cognitoUserSession)
		}
	} else {
		console.error("No signInUserSession in:", cognitoUserSession)
	}
	return null
}

const _handleSuccessfulAuth = async (password, cognitoSignInUserSession) => {
	// Runs after successful login (not on page refresh)
	const idToken = extractIdToken(cognitoSignInUserSession)
	if (idToken) {
		api.setIdTokens(idToken)
		await api.verifyAuth(password)
		return true
	}
	console.warn("No id token. A new password may be required by Cognito.")
	return false
}

const _handleAuthFailure = (reason) => {
	console.error("auth failure:", reason)
	// bubbling up error to present to user in snackbar
	throw new Error(reason.message)
}

export const signInWithEmailAndPassword = (email, password) => async (dispatch) => {
	try {
		const result = await authRef.signIn({ username: email, password })
		const cognitoSignInUserSession = result["signInUserSession"]
		if (cognitoSignInUserSession !== null) {
			// This represents a flow where the user successfully signs in and has a valid session
			// e.g. not requiring MFA or a new password
			const isSignedIn = await _handleSuccessfulAuth(password, cognitoSignInUserSession)
			if (isSignedIn) {
				dispatch(fetchLayoutDetails())
			}
		}
		return result
	} catch (e) {
		_handleAuthFailure(e)
	}
}

export const completeNewPassword = async (user, newPassword) => {
	try {
		return await authRef.completeNewPassword(user, newPassword)
	} catch (e) {
		_handleAuthFailure(e)
	}
}

export const forgotPassword = (email) => async (_dispatch) => {
	try {
		return await authRef.forgotPassword(email)
	} catch (e) {
		_handleAuthFailure(e)
	}
}

export const forgotPasswordSubmit = (email, code, newPassword) => (_dispatch) => {
	try {
		return authRef.forgotPasswordSubmit(email, code, newPassword)
	} catch (e) {
		_handleAuthFailure(e)
	}
}
export const generateTotpUri = async (user) => {
	let secret = ""
	const account = user.username
	try {
		secret = await authRef.setupTOTP(user)
		return {
			uri: `://totp/genius-tracker:${account}?secret=${secret}&issuer=genius-tracker`,
			secret: secret,
		}
	} catch (e) {
		_handleAuthFailure(e)
	}
}

export const verifyToken = (user, code, password, authenticationStep) => async (dispatch) => {
	let cognitoUserSession = {}

	try {
		if (authenticationStep === "MFA_SETUP") {
			cognitoUserSession = await authRef.verifyTotpToken(user, code)
		} else {
			cognitoUserSession = await authRef.confirmSignIn(user, code, authenticationStep)
		}
	} catch (e) {
		_handleAuthFailure(e)
	}
	const isSignedIn = await _handleSuccessfulAuth(
		password,
		authenticationStep === "MFA_SETUP" ? cognitoUserSession : cognitoUserSession["signInUserSession"],
	)
	if (isSignedIn) {
		dispatch(fetchLayoutDetails())
	}
}
